import type { ColumnInfo, ItemInfo } from '../api/client'

/**
 * M9.6：公式列的**前端**解析与求值。
 *
 * 必须与服务端 `Formula.cpp` 保持同一套语义（两处都改才能不一致）：
 *   · {标题} 引用，找不到按列 key 兜底
 *   · 空值参与运算 → 结果仍为空（不是 0）
 *   · SUM/AVG/MIN/MAX/COUNT(列引用) 对**整列**聚合，忽略空值，全空 → 空
 *   · DATEDIF(列,列,"d"|"m"|"y")
 *   · 错误态：#ERROR! / #REF! / #DIV/0!（#CYCLE! 由依赖图那边标）
 */

export const FORMULA_ERRORS = ['#ERROR!', '#REF!', '#DIV/0!', '#CYCLE!'] as const
export function isFormulaError(v: string): boolean {
  return (FORMULA_ERRORS as readonly string[]).includes(v)
}

type Node =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'ref'; name: string }
  | { t: 'func'; name: string; args: Node[] }
  | { t: 'bin'; op: string; l: Node; r: Node }
  | { t: 'neg'; x: Node }

type Token =
  | { k: 'num'; v: number }
  | { k: 'ident'; v: string }
  | { k: 'ref'; v: string }
  | { k: 'str'; v: string }
  | { k: 'op'; v: string }
  | { k: 'end' }

function lex(src: string): Token[] {
  const out: Token[] = []
  let i = 0
  while (i < src.length) {
    const c = src.charAt(i)
    if (/\s/.test(c)) {
      i += 1
      continue
    }
    if (/[0-9.]/.test(c)) {
      let j = i
      while (j < src.length && /[0-9.]/.test(src.charAt(j))) j += 1
      out.push({ k: 'num', v: Number(src.slice(i, j)) })
      i = j
      continue
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i
      while (j < src.length && /[A-Za-z0-9_]/.test(src.charAt(j))) j += 1
      out.push({ k: 'ident', v: src.slice(i, j) })
      i = j
      continue
    }
    if (c === '{') {
      const close = src.indexOf('}', i + 1)
      if (close < 0) throw new Error('列引用缺少 }')
      out.push({ k: 'ref', v: src.slice(i + 1, close) })
      i = close + 1
      continue
    }
    if (c === '"') {
      const close = src.indexOf('"', i + 1)
      if (close < 0) throw new Error('字符串缺少结尾引号')
      out.push({ k: 'str', v: src.slice(i + 1, close) })
      i = close + 1
      continue
    }
    if ('+-*/(),'.includes(c)) {
      out.push({ k: 'op', v: c })
      i += 1
      continue
    }
    throw new Error(`无法识别的字符 '${c}'`)
  }
  out.push({ k: 'end' })
  return out
}

class P {
  private i = 0
  constructor(private readonly tk: Token[]) {}

  private peek(): Token {
    return this.tk[this.i] as Token
  }
  private isOp(v: string): boolean {
    const t = this.peek()
    return t.k === 'op' && t.v === v
  }
  private next(): Token {
    return this.tk[this.i++] as Token
  }

  parse(): Node {
    const n = this.expr()
    if (this.peek().k !== 'end') throw new Error('表达式末尾有多余内容')
    return n
  }

  private expr(): Node {
    let l = this.term()
    while (this.isOp('+') || this.isOp('-')) {
      const op = (this.next() as { v: string }).v
      l = { t: 'bin', op, l, r: this.term() }
    }
    return l
  }

  private term(): Node {
    let l = this.unary()
    while (this.isOp('*') || this.isOp('/')) {
      const op = (this.next() as { v: string }).v
      l = { t: 'bin', op, l, r: this.unary() }
    }
    return l
  }

  private unary(): Node {
    if (this.isOp('-')) {
      this.next()
      return { t: 'neg', x: this.unary() }
    }
    return this.primary()
  }

  private primary(): Node {
    const t = this.next()
    if (t.k === 'num') return { t: 'num', v: t.v }
    if (t.k === 'str') return { t: 'str', v: t.v }
    if (t.k === 'ref') return { t: 'ref', name: t.v }
    if (t.k === 'ident') {
      if (!this.isOp('(')) throw new Error('函数名后面要跟 (')
      this.next()
      const args: Node[] = []
      if (!this.isOp(')')) {
        for (;;) {
          args.push(this.expr())
          if (this.isOp(',')) {
            this.next()
            continue
          }
          break
        }
      }
      if (!this.isOp(')')) throw new Error('函数调用缺少 )')
      this.next()
      return { t: 'func', name: t.v, args }
    }
    if (t.k === 'op' && t.v === '(') {
      const inner = this.expr()
      if (!this.isOp(')')) throw new Error('括号不匹配')
      this.next()
      return inner
    }
    throw new Error('这里需要一个数字、列引用或函数')
  }
}

export interface ParsedFormula {
  ok: boolean
  error?: string
  ast?: Node
}

export function parseFormula(src: string): ParsedFormula {
  if (src.trim() === '') return { ok: false, error: '公式为空' }
  try {
    return { ok: true, ast: new P(lex(src)).parse() }
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : '公式语法错误' }
  }
}

function collectRefs(n: Node, out: string[]): void {
  if (n.t === 'ref' && !out.includes(n.name)) out.push(n.name)
  if (n.t === 'func') n.args.forEach((a) => collectRefs(a, out))
  if (n.t === 'bin') {
    collectRefs(n.l, out)
    collectRefs(n.r, out)
  }
  if (n.t === 'neg') collectRefs(n.x, out)
}

export function formulaReferences(parsed: ParsedFormula): string[] {
  const out: string[] = []
  if (parsed.ok && parsed.ast !== undefined) collectRefs(parsed.ast, out)
  return out
}

type Val =
  | { kind: 'empty' }
  | { kind: 'num'; n: number }
  | { kind: 'text'; s: string }
  // 引用到的单元格本身就是错误态（#REF! / #CYCLE! …）时要**继续往外传播**
  | { kind: 'err'; code: string }

const EMPTY: Val = { kind: 'empty' }

function cellVal(values: Record<string, string>, key: string): Val {
  const raw = (values[key] ?? '').trim()
  if (raw === '') return EMPTY
  // 错误态字符串（#REF! / #CYCLE! / #DIV/0! / #ERROR!）原样传播
  if (raw.startsWith('#') && raw.endsWith('!')) return { kind: 'err', code: raw }
  const n = Number(raw)
  return Number.isFinite(n) ? { kind: 'num', n } : { kind: 'text', s: raw }
}

function findColumn(columns: ColumnInfo[], name: string): ColumnInfo | undefined {
  return columns.find((c) => c.title === name) ?? columns.find((c) => c.key === name)
}

function toCellString(v: Val): string {
  if (v.kind === 'empty') return ''
  if (v.kind === 'err') return v.code
  if (v.kind === 'text') return v.s
  if (Math.abs(v.n - Math.round(v.n)) < 1e-9) return String(Math.round(v.n))
  return String(Number(v.n.toFixed(6)))
}

function containsAggregate(n: Node): boolean {
  if (n.t === 'func' && ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'].includes(n.name.toUpperCase())) return true
  if (n.t === 'func') return n.args.some(containsAggregate)
  if (n.t === 'bin') return containsAggregate(n.l) || containsAggregate(n.r)
  if (n.t === 'neg') return containsAggregate(n.x)
  return false
}

export interface EvalEnv {
  columns: ColumnInfo[]
  items: ItemInfo[]
  /** 当前求值到的条目 values（可能已被公式列覆盖） */
  values: Record<string, string>
  /**
   * 取某个条目某列的"当前值"。
   * 聚合（SUM/AVG/…）必须走这个解析器，否则改了一个单元格后，
   * 引用它的聚合公式仍是旧值（实测踩过：合计停在 260 不变）。
   */
  resolve?: (item: ItemInfo, key: string) => string
  /** 取某条目"已经算好的派生值补丁"（聚合里按条目求值表达式时用） */
  patchFor?: (itemId: number) => Record<string, string> | undefined
}

function parseDateDays(raw: string): number | null {
  const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(raw.trim())
  if (m === null) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
  return Math.floor(Date.UTC(y, mo - 1, d) / 86400000)
}

function evalNode(n: Node, env: EvalEnv): { v: Val; err?: string } {
  switch (n.t) {
    case 'num':
      return { v: { kind: 'num', n: n.v } }
    case 'str':
      return { v: { kind: 'text', s: n.v } }
    case 'ref': {
      const col = findColumn(env.columns, n.name)
      if (col === undefined) return { v: EMPTY, err: '#REF!' }
      return { v: cellVal(env.values, col.key) }
    }
    case 'neg': {
      const r = evalNode(n.x, env)
      if (r.err !== undefined) return r
      if (r.v.kind === 'err') return { v: r.v }
      if (r.v.kind === 'empty') return { v: EMPTY }
      if (r.v.kind !== 'num') return { v: EMPTY, err: '#ERROR!' }
      return { v: { kind: 'num', n: -r.v.n } }
    }
    case 'bin': {
      const a = evalNode(n.l, env)
      if (a.err !== undefined) return a
      if (a.v.kind === 'err') return { v: a.v }
      const b = evalNode(n.r, env)
      if (b.err !== undefined) return b
      if (b.v.kind === 'err') return { v: b.v }
      if (a.v.kind === 'empty' || b.v.kind === 'empty') return { v: EMPTY }
      if (a.v.kind !== 'num' || b.v.kind !== 'num') return { v: EMPTY, err: '#ERROR!' }
      const x = a.v.n
      const y = b.v.n
      if (n.op === '+') return { v: { kind: 'num', n: x + y } }
      if (n.op === '-') return { v: { kind: 'num', n: x - y } }
      if (n.op === '*') return { v: { kind: 'num', n: x * y } }
      if (y === 0) return { v: EMPTY, err: '#DIV/0!' }
      return { v: { kind: 'num', n: x / y } }
    }
    case 'func': {
      const name = n.name.toUpperCase()
      if (['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'].includes(name)) {
        if (n.args.length !== 1) return { v: EMPTY, err: '#ERROR!' }
        const arg = n.args[0] as Node
        if (containsAggregate(arg)) return { v: EMPTY, err: '#ERROR!' }	// 聚合不能套聚合
        const nums: number[] = []
        if (arg.t === 'ref') {
          // 列引用 → 整列聚合
          const col = findColumn(env.columns, arg.name)
          if (col === undefined) return { v: EMPTY, err: '#REF!' }
          for (const it of env.items) {
            const raw = (env.resolve !== undefined ? env.resolve(it, col.key) : it.values[col.key] ?? '').trim()
            if (raw === '') continue
            // 整列里只要有一个单元格是错误态，聚合结果就是那个错误（不能把错误当 0 吞掉）
            if (raw.startsWith('#') && raw.endsWith('!')) return { v: { kind: 'err', code: raw } }
            const v = Number(raw)
            if (Number.isFinite(v)) nums.push(v)
          }
        } else {
          // 表达式参数（如 SUM({a}*{b})）→ 逐条目求值再聚合
          for (const it of env.items) {
            const r = evalNode(arg, { ...env, values: { ...it.values, ...(env.patchFor?.(it.id) ?? {}) } })
            if (r.err !== undefined) return r
            if (r.v.kind === 'err') return { v: r.v }
            if (r.v.kind === 'num') nums.push(r.v.n)
          }
        }
        if (name === 'COUNT') return { v: { kind: 'num', n: nums.length } }
        if (nums.length === 0) return { v: EMPTY }
        const sum = nums.reduce((s, v) => s + v, 0)
        if (name === 'SUM') return { v: { kind: 'num', n: sum } }
        if (name === 'AVG') return { v: { kind: 'num', n: sum / nums.length } }
        if (name === 'MIN') return { v: { kind: 'num', n: Math.min(...nums) } }
        return { v: { kind: 'num', n: Math.max(...nums) } }
      }
      if (name === 'ROUND') {
        if (n.args.length < 1 || n.args.length > 2) return { v: EMPTY, err: '#ERROR!' }
        const v = evalNode(n.args[0] as Node, env)
        if (v.err !== undefined) return v
        if (v.v.kind === 'err') return { v: v.v }
        if (v.v.kind === 'empty') return { v: EMPTY }
        if (v.v.kind !== 'num') return { v: EMPTY, err: '#ERROR!' }
        let digits = 0
        if (n.args.length === 2) {
          const d = evalNode(n.args[1] as Node, env)
          if (d.err !== undefined) return d
          if (d.v.kind !== 'num') return { v: EMPTY, err: '#ERROR!' }
          digits = d.v.n
        }
        const mul = Math.pow(10, digits)
        return { v: { kind: 'num', n: Math.round(v.v.n * mul) / mul } }
      }
      if (name === 'ABS') {
        if (n.args.length !== 1) return { v: EMPTY, err: '#ERROR!' }
        const v = evalNode(n.args[0] as Node, env)
        if (v.err !== undefined) return v
        if (v.v.kind === 'err') return { v: v.v }
        if (v.v.kind === 'empty') return { v: EMPTY }
        if (v.v.kind !== 'num') return { v: EMPTY, err: '#ERROR!' }
        return { v: { kind: 'num', n: Math.abs(v.v.n) } }
      }
      if (name === 'DATEDIF') {
        if (n.args.length !== 3 || (n.args[0] as Node).t !== 'ref' || (n.args[1] as Node).t !== 'ref' || (n.args[2] as Node).t !== 'str') {
          return { v: EMPTY, err: '#ERROR!' }
        }
        const c1 = findColumn(env.columns, (n.args[0] as { name: string }).name)
        const c2 = findColumn(env.columns, (n.args[1] as { name: string }).name)
        if (c1 === undefined || c2 === undefined) return { v: EMPTY, err: '#REF!' }
        const v1 = cellVal(env.values, c1.key)
        const v2 = cellVal(env.values, c2.key)
        if (v1.kind === 'empty' || v2.kind === 'empty') return { v: EMPTY }
        const s1 = v1.kind === 'text' ? v1.s : toCellString(v1)
        const s2 = v2.kind === 'text' ? v2.s : toCellString(v2)
        const d1 = parseDateDays(s1)
        const d2 = parseDateDays(s2)
        if (d1 === null || d2 === null) return { v: EMPTY, err: '#ERROR!' }
        const unit = (n.args[2] as { v: string }).v
        if (unit === 'd') return { v: { kind: 'num', n: d2 - d1 } }
        const m1 = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(s1.trim())
        const m2 = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/.exec(s2.trim())
        if (m1 === null || m2 === null) return { v: EMPTY, err: '#ERROR!' }
        const [y1, mo1, dd1] = [Number(m1[1]), Number(m1[2]), Number(m1[3])]
        const [y2, mo2, dd2] = [Number(m2[1]), Number(m2[2]), Number(m2[3])]
        if (unit === 'm') {
          let months = (y2 - y1) * 12 + (mo2 - mo1)
          if (dd2 < dd1) months -= 1
          return { v: { kind: 'num', n: months } }
        }
        if (unit === 'y') {
          let years = y2 - y1
          if (mo2 < mo1 || (mo2 === mo1 && dd2 < dd1)) years -= 1
          return { v: { kind: 'num', n: years } }
        }
        return { v: EMPTY, err: '#ERROR!' }
      }
      return { v: EMPTY, err: '#ERROR!' }
    }
  }
}

/** 求值单个公式；返回写回单元格的字符串（可能是 #REF! 等错误态） */
export function evaluateFormula(parsed: ParsedFormula, env: EvalEnv): string {
  if (!parsed.ok || parsed.ast === undefined) return '#ERROR!'
  const r = evalNode(parsed.ast, env)
  if (r.err !== undefined) return r.err
  return toCellString(r.v)
}
